import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import type { Severity, SystemMapEdge, SystemMapNode } from "../api/types";
import { SEVERITY_COLOR, SEVERITY_ORDER } from "../graph/palette";
import { Icon } from "../components/icons";

// System map canvas. The whole type graph as a living force layout, drawn on a
// <canvas> so hundreds of nodes and edges stay smooth:
//
// - pan (drag the background), zoom at the cursor (wheel / trackpad pinch / two
//   fingers), zoom buttons, fit, fullscreen;
// - reshape: drag a node and the layout follows; the node stays pinned where you
//   drop it (double-click releases it, "Re-layout" releases everything);
// - quiet, Graphify-like marks: small matte nodes sized by connections, hairline
//   edges, labels only where they fit (hubs at rest, more as you zoom in);
// - a node with a confirmed finding is filled with its severity color and glows.

interface SimNode extends SimulationNodeDatum {
  id: string;
  r: number;
}
type SimLink = SimulationLinkDatum<SimNode> & { w: number };

export interface MapCanvasProps {
  nodes: SystemMapNode[];
  edges: SystemMapEdge[];
  colorOf: (n: SystemMapNode) => string;
  selected: string | null;
  onSelect: (id: string | null) => void;
  matches: Set<string> | null; // active filter (null = no filter)
  affected: Set<string> | null; // affected-set highlight
  focusRequest: { id: string; seq: number } | null; // center on a node (rail click)
  ariaLabel: string;
}

const BG = "#0e0d10";
const EDGE = "232, 226, 220";
const LABEL = "#c9c2bc";
const LABEL_LEAD = "#f4f0ec";
const SEV_RANK: Record<Severity, number> = { critical: 5, high: 4, medium: 3, low: 2, info: 1 };

function radiusFor(degree: number) {
  return Math.min(2.4 + Math.sqrt(degree) * 1.35, 12);
}

// Deterministic randomness so the same snapshot always settles the same way.
function lcg(seed: number) {
  let s = seed >>> 0 || 1;
  return () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
}

export function MapCanvas(props: MapCanvasProps) {
  const { nodes, edges, colorOf, selected, onSelect, matches, affected, focusRequest, ariaLabel } = props;

  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const simNodes = useRef<SimNode[]>([]);
  const view = useRef({ k: 1, x: 0, y: 0 }); // screen = world * k + (x, y)
  const size = useRef({ w: 800, h: 600, dpr: 1 });
  const frame = useRef(0);
  const hoverRef = useRef<string | null>(null);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const [pinned, setPinned] = useState(0);
  const [full, setFull] = useState(false);
  const [announce, setAnnounce] = useState("");

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const anyFinding = useMemo(() => nodes.some((n) => n.severity), [nodes]);
  const neighbours = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const [s, t] of edges) {
      if (!map.has(s)) map.set(s, new Set());
      if (!map.has(t)) map.set(t, new Set());
      map.get(s)!.add(t);
      map.get(t)!.add(s);
    }
    return map;
  }, [edges]);
  const hubs = useMemo(
    () => new Set([...nodes].sort((a, b) => b.degree - a.degree).slice(0, 10).map((n) => n.id)),
    [nodes],
  );

  // Keep the latest props reachable from the (stable) draw function.
  const live = useRef(props);
  live.current = props;
  const derived = useRef({ byId, anyFinding, neighbours, hubs });
  derived.current = { byId, anyFinding, neighbours, hubs };

  const reduceMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // ---------------------------------------------------------------- drawing
  const draw = useCallback(() => {
    frame.current = 0;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { w, h, dpr } = size.current;
    const { k, x: tx, y: ty } = view.current;
    const { selected: sel, matches: m, affected: aff, colorOf: color } = live.current;
    const { byId: ids, anyFinding: anyF, neighbours: nb, hubs: hb } = derived.current;
    const focus = sel ?? hoverRef.current;
    const focusSet = focus ? nb.get(focus) ?? new Set<string>() : null;
    const pos = new Map(simNodes.current.map((n) => [n.id, n]));
    // Blink: a time phase so severity nodes breathe. Static (no phase) under
    // prefers-reduced-motion — the pulse loop (below) is not started there.
    const now = performance.now();
    const pulsing = anyF && !reduceMotion;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, w, h);

    const sx = (n: SimNode) => (n.x ?? 0) * k + tx;
    const sy = (n: SimNode) => (n.y ?? 0) * k + ty;

    // Edges: one hairline batch, then the focused node's edges on top.
    ctx.lineWidth = 0.75;
    ctx.strokeStyle = `rgba(${EDGE}, ${focus ? 0.035 : m ? 0.05 : 0.13})`;
    ctx.beginPath();
    const hot: [SimNode, SimNode][] = [];
    for (const [s, t] of live.current.edges) {
      const a = pos.get(s);
      const b = pos.get(t);
      if (!a || !b) continue;
      if (focus && (s === focus || t === focus)) { hot.push([a, b]); continue; }
      ctx.moveTo(sx(a), sy(a));
      ctx.lineTo(sx(b), sy(b));
    }
    ctx.stroke();
    if (hot.length) {
      ctx.strokeStyle = `rgba(${EDGE}, 0.62)`;
      ctx.fillStyle = `rgba(${EDGE}, 0.75)`;
      ctx.lineWidth = 1;
      for (const [a, b] of hot) {
        const ax = sx(a), ay = sy(a), bx = sx(b), by = sy(b);
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
        // Direction: a small arrowhead at the dependency's edge.
        const ang = Math.atan2(by - ay, bx - ax);
        const rb = b.r * k + 2;
        const hx = bx - Math.cos(ang) * rb, hy = by - Math.sin(ang) * rb;
        ctx.beginPath();
        ctx.moveTo(hx, hy);
        ctx.lineTo(hx - Math.cos(ang - 0.42) * 6, hy - Math.sin(ang - 0.42) * 6);
        ctx.lineTo(hx - Math.cos(ang + 0.42) * 6, hy - Math.sin(ang + 0.42) * 6);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Nodes. Severity nodes last so they sit on top.
    const order = [...simNodes.current].sort((a, b) => {
      const sa = ids.get(a.id)?.severity, sb = ids.get(b.id)?.severity;
      return (sa ? SEV_RANK[sa] : 0) - (sb ? SEV_RANK[sb] : 0);
    });
    for (const n of order) {
      const meta = ids.get(n.id);
      if (!meta) continue;
      const x = sx(n), y = sy(n);
      const r = Math.max(1.3, n.r * k);
      if (x < -20 || y < -20 || x > w + 20 || y > h + 20) continue;
      let alpha = 1;
      if (anyF && !meta.severity) alpha = 0.55;
      if (m && !m.has(n.id)) alpha = 0.1;
      if (focusSet && n.id !== focus && !focusSet.has(n.id)) alpha = Math.min(alpha, 0.13);
      ctx.globalAlpha = alpha;
      if (meta.severity) {
        const c = SEVERITY_COLOR[meta.severity];
        // Blink the glow so a new / severe finding draws the eye. Critical pulses
        // strongest and fastest; info barely moves. amp scales by severity rank so
        // the graph does not strobe for low findings. pulse = 1 when not pulsing
        // (reduced motion) → a steady, severity-scaled glow.
        const speed = meta.severity === "critical" ? 0.0060
          : meta.severity === "high" ? 0.0045
          : meta.severity === "medium" ? 0.0034 : 0.0024;
        const amp = SEV_RANK[meta.severity] / 5; // 1 (critical) .. 0.2 (info)
        const pulse = pulsing ? 0.5 + 0.5 * Math.sin(now * speed) : 1;
        ctx.shadowColor = c;
        ctx.shadowBlur = 6 + 26 * pulse * amp;
        ctx.globalAlpha = alpha * (1 - 0.5 * amp * (1 - pulse));
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(x, y, r + 1.6 + 3.6 * pulse * amp, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = alpha;
      } else {
        ctx.fillStyle = color(meta);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      const ringR = r + (meta.severity ? 4.5 : 2.8);
      if (n.id === sel) {
        ctx.globalAlpha = 1;
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(x, y, ringR, 0, Math.PI * 2); ctx.stroke();
      } else if (n.fx != null) {
        ctx.strokeStyle = "rgba(244, 240, 236, 0.55)";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, ringR, 0, Math.PI * 2); ctx.stroke();
      }
      if (aff?.has(n.id)) {
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = "#ffffff";
        ctx.setLineDash([2, 2.5]);
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, ringR + 2, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]);
      }
      if (n.id === hoverRef.current && n.id !== sel) {
        ctx.globalAlpha = 0.8;
        ctx.strokeStyle = LABEL;
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, ringR, 0, Math.PI * 2); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

    // Labels: priority order, drawn only where they do not collide.
    const cand: { n: SimNode; p: number; lead: boolean }[] = [];
    for (const n of simNodes.current) {
      const meta = ids.get(n.id);
      if (!meta) continue;
      let p = -1;
      if (n.id === sel) p = 10000;
      else if (n.id === hoverRef.current) p = 9000;
      else if (meta.severity) p = 8000 + SEV_RANK[meta.severity] * 100 + meta.degree;
      else if (focusSet?.has(n.id)) p = 5000 + meta.degree;
      else if (m?.has(n.id)) p = 4000 + meta.degree;
      else if (!focusSet && !m && hb.has(n.id)) p = 3000 + meta.degree;
      else if (!focusSet && !m && n.r * k > 6.5) p = 1000 + meta.degree;
      if (p >= 0) cand.push({ n, p, lead: n.id === sel || n.id === hoverRef.current });
    }
    cand.sort((a, b) => b.p - a.p);
    const placed: [number, number, number, number][] = [];
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    for (const { n, lead } of cand.slice(0, 160)) {
      const meta = ids.get(n.id)!;
      ctx.font = `${lead ? 600 : 500} ${lead ? 12 : 11}px Inter, system-ui, sans-serif`;
      const text = meta.label;
      const tw = ctx.measureText(text).width;
      const x = sx(n) + n.r * k + 6, y = sy(n);
      const box: [number, number, number, number] = [x - 2, y - 8, x + tw + 2, y + 8];
      if (!lead && placed.some((b) => box[0] < b[2] && box[2] > b[0] && box[1] < b[3] && box[3] > b[1])) continue;
      if (box[2] < 0 || box[0] > w || box[3] < 0 || box[1] > h) continue;
      placed.push(box);
      ctx.strokeStyle = BG;
      ctx.lineWidth = 3.5;
      ctx.strokeText(text, x, y);
      ctx.fillStyle = lead ? LABEL_LEAD : LABEL;
      ctx.fillText(text, x, y);
    }
  }, []);

  const redraw = useCallback(() => {
    if (!frame.current) frame.current = requestAnimationFrame(draw);
  }, [draw]);

  // Redraw on visual prop changes.
  useEffect(() => { redraw(); }, [selected, matches, affected, colorOf, redraw]);

  // Blink loop: while any finding is present (and motion is allowed), keep
  // redrawing so the severity glow breathes. Throttled to ~30fps (the pulse is
  // slow), and rAF pauses itself when the tab is hidden. Off entirely under
  // prefers-reduced-motion or when there are no findings.
  useEffect(() => {
    if (reduceMotion || !anyFinding) return;
    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      if (t - last >= 33) { last = t; draw(); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [anyFinding, reduceMotion, draw]);

  // ------------------------------------------------------------- view helpers
  const fit = useCallback((ids?: Set<string>, animate = true) => {
    const pts = simNodes.current.filter((n) => !ids || ids.has(n.id));
    if (!pts.length) return;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const n of pts) {
      minX = Math.min(minX, (n.x ?? 0) - n.r); maxX = Math.max(maxX, (n.x ?? 0) + n.r);
      minY = Math.min(minY, (n.y ?? 0) - n.r); maxY = Math.max(maxY, (n.y ?? 0) + n.r);
    }
    const { w, h } = size.current;
    const pad = 48;
    const k = Math.min(4, Math.max(0.12, Math.min((w - pad * 2) / Math.max(1, maxX - minX), (h - pad * 2) / Math.max(1, maxY - minY))));
    const target = { k, x: w / 2 - ((minX + maxX) / 2) * k, y: h / 2 - ((minY + maxY) / 2) * k };
    animateTo(target, animate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anim = useRef(0);
  // Once the person pans, zooms or drags, the view is theirs: no auto-fit.
  const userMoved = useRef(false);
  const animateTo = (target: { k: number; x: number; y: number }, animate: boolean) => {
    cancelAnimationFrame(anim.current);
    if (!animate || reduceMotion) { view.current = target; redraw(); return; }
    const from = { ...view.current };
    const t0 = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / 380);
      const e = 1 - Math.pow(1 - p, 4); // exponential-ish ease-out
      view.current = { k: from.k + (target.k - from.k) * e, x: from.x + (target.x - from.x) * e, y: from.y + (target.y - from.y) * e };
      draw();
      if (p < 1) anim.current = requestAnimationFrame(step);
    };
    anim.current = requestAnimationFrame(step);
  };

  const zoomAt = useCallback((factor: number, cx: number, cy: number) => {
    userMoved.current = true;
    const v = view.current;
    const k = Math.min(8, Math.max(0.1, v.k * factor));
    const f = k / v.k;
    view.current = { k, x: cx - (cx - v.x) * f, y: cy - (cy - v.y) * f };
    redraw();
  }, [redraw]);

  // ------------------------------------------------------------- simulation
  useEffect(() => {
    const rand = lcg(nodes.length * 7919 + edges.length);
    // Seed each community on its own sector so related types start together.
    const comms = [...new Set(nodes.map((n) => n.community ?? -1))].sort((a, b) => a - b);
    const angle = new Map(comms.map((c, i) => [c, (i / comms.length) * Math.PI * 2]));
    const R = 40 + Math.sqrt(nodes.length) * 22;
    const prev = new Map(simNodes.current.map((n) => [n.id, n]));
    const sn: SimNode[] = nodes.map((n) => {
      const p = prev.get(n.id);
      if (p) return { ...p, r: radiusFor(n.degree) };
      const a = (angle.get(n.community ?? -1) ?? 0) + (rand() - 0.5) * 0.9;
      const d = n.degree === 0 ? R * (1.05 + rand() * 0.35) : R * (0.2 + rand() * 0.75);
      return { id: n.id, r: radiusFor(n.degree), x: Math.cos(a) * d, y: Math.sin(a) * d };
    });
    simNodes.current = sn;
    const idSet = new Set(sn.map((n) => n.id));
    const links: SimLink[] = edges
      .filter(([s, t]) => idSet.has(s) && idSet.has(t))
      .map(([s, t, w]) => ({ source: s, target: t, w }));

    const sim = forceSimulation<SimNode, SimLink>(sn)
      .randomSource(lcg(42))
      .force("charge", forceManyBody<SimNode>().strength((n) => -22 - n.r * 5).distanceMax(520).theta(0.9))
      .force("link", forceLink<SimNode, SimLink>(links).id((n) => n.id).distance(30).strength((l) => Math.min(0.7, 0.12 + Math.log2(1 + l.w) * 0.12)))
      .force("collide", forceCollide<SimNode>((n) => n.r + 1.6).strength(0.8))
      .force("x", forceX<SimNode>(0).strength(0.045))
      .force("y", forceY<SimNode>(0).strength(0.06))
      .alphaDecay(0.03)
      .stop();
    simRef.current = sim;

    // Settle most of the way off-screen, then let the last stretch animate in.
    const warm = reduceMotion ? 400 : 160;
    for (let i = 0; i < warm; i++) sim.tick();
    fit(undefined, false);
    userMoved.current = false;
    sim.on("tick", redraw);
    sim.on("end", () => { if (!userMoved.current) fit(); });
    if (!reduceMotion) sim.restart();
    else redraw();
    return () => { sim.stop(); sim.on("tick", null); sim.on("end", null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  // Canvas size follows its box (and fullscreen).
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ro = new ResizeObserver(() => {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      size.current = { w: r.width, h: r.height, dpr };
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      redraw();
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [redraw]);

  useEffect(() => {
    const onFs = () => { setFull(document.fullscreenElement === wrapRef.current); setTimeout(() => fit(), 60); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [fit]);

  // Center on a node asked for from outside (rail, search).
  useEffect(() => {
    if (!focusRequest) return;
    const n = simNodes.current.find((s) => s.id === focusRequest.id);
    if (!n) return;
    const { w, h } = size.current;
    const k = Math.max(view.current.k, 1.4);
    animateTo({ k, x: w / 2 - (n.x ?? 0) * k, y: h / 2 - (n.y ?? 0) * k }, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  // Selection announcement for assistive tech.
  useEffect(() => {
    if (!selected) { setAnnounce(""); return; }
    const n = byId.get(selected);
    if (n) setAnnounce(`Selected ${n.label}: ${neighbours.get(selected)?.size ?? 0} connected types${n.severity ? `, ${n.severity} finding` : ""}.`);
  }, [selected, byId, neighbours]);

  // ------------------------------------------------------------- pointer input
  const toWorld = (cx: number, cy: number) => {
    const { k, x, y } = view.current;
    return { x: (cx - x) / k, y: (cy - y) / k };
  };
  const local = (e: { clientX: number; clientY: number }) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const hit = (cx: number, cy: number): SimNode | null => {
    const sim = simRef.current;
    if (!sim) return null;
    const p = toWorld(cx, cy);
    const n = sim.find(p.x, p.y, 14 / view.current.k + 4);
    return n ?? null;
  };

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<
    | { kind: "pan"; sx: number; sy: number; vx: number; vy: number; moved: boolean }
    | { kind: "node"; node: SimNode; sx: number; sy: number; moved: boolean; wasPinned: boolean }
    | { kind: "pinch"; d: number; cx: number; cy: number }
    | null
  >(null);

  const onPointerDown = (e: React.PointerEvent) => {
    canvasRef.current?.setPointerCapture(e.pointerId);
    setHover(null); // the tooltip never follows a gesture
    const p = local(e);
    pointers.current.set(e.pointerId, p);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { kind: "pinch", d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
      return;
    }
    const n = hit(p.x, p.y);
    if (n) {
      gesture.current = { kind: "node", node: n, sx: p.x, sy: p.y, moved: false, wasPinned: n.fx != null };
    } else {
      gesture.current = { kind: "pan", sx: p.x, sy: p.y, vx: view.current.x, vy: view.current.y, moved: false };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const p = local(e);
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, p);
    const g = gesture.current;
    if (g?.kind === "pinch" && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      zoomAt(d / g.d, g.cx, g.cy);
      g.d = d;
      return;
    }
    if (g?.kind === "pan") {
      if (Math.abs(p.x - g.sx) + Math.abs(p.y - g.sy) > 3) { g.moved = true; userMoved.current = true; }
      view.current = { ...view.current, x: g.vx + (p.x - g.sx), y: g.vy + (p.y - g.sy) };
      redraw();
      return;
    }
    if (g?.kind === "node") {
      if (!g.moved && Math.abs(p.x - g.sx) + Math.abs(p.y - g.sy) > 3) {
        g.moved = true;
        userMoved.current = true;
        simRef.current?.alphaTarget(0.22).restart();
      }
      if (g.moved) {
        const w = toWorld(p.x, p.y);
        g.node.fx = w.x;
        g.node.fy = w.y;
        redraw();
      }
      return;
    }
    // Hover.
    const n = hit(p.x, p.y);
    const id = n?.id ?? null;
    if (id !== hoverRef.current) {
      hoverRef.current = id;
      redraw();
    }
    setHover(id ? { id, x: p.x, y: p.y } : null);
  };

  const endGesture = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    gesture.current = null;
    hoverRef.current = null;
    setHover(null);
    if (!g) return;
    if (g.kind === "node") {
      simRef.current?.alphaTarget(0);
      if (g.moved) {
        setPinned(simNodes.current.filter((n) => n.fx != null).length);
      } else {
        onSelect(g.node.id === live.current.selected ? null : g.node.id);
      }
    } else if (g.kind === "pan" && !g.moved) {
      onSelect(null);
    }
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    const p = local(e);
    const n = hit(p.x, p.y);
    if (n && n.fx != null) {
      n.fx = null; n.fy = null;
      simRef.current?.alpha(0.3).restart();
      setPinned(simNodes.current.filter((s) => s.fx != null).length);
    } else if (!n) {
      zoomAt(1.6, p.x, p.y);
    }
  };

  // Wheel must be non-passive to stop the page from scrolling.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const scale = e.ctrlKey ? 0.012 : 0.0016; // trackpad pinch arrives as ctrl+wheel
      zoomAt(Math.exp(-e.deltaY * scale), e.clientX - r.left, e.clientY - r.top);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // ------------------------------------------------------------- commands
  const zoomCenter = (f: number) => zoomAt(f, size.current.w / 2, size.current.h / 2);
  const relayout = () => {
    for (const n of simNodes.current) { n.fx = null; n.fy = null; }
    setPinned(0);
    userMoved.current = false; // fit again once it settles
    simRef.current?.alpha(0.9).restart();
  };
  const toggleFull = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void wrapRef.current?.requestFullscreen?.();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = 60;
    const v = view.current;
    switch (e.key) {
      case "+": case "=": zoomCenter(1.25); break;
      case "-": case "_": zoomCenter(0.8); break;
      case "0": fit(); break;
      case "f": case "F": toggleFull(); break;
      case "r": case "R": relayout(); break;
      case "Escape": onSelect(null); break;
      case "ArrowLeft": view.current = { ...v, x: v.x + step }; redraw(); break;
      case "ArrowRight": view.current = { ...v, x: v.x - step }; redraw(); break;
      case "ArrowUp": view.current = { ...v, y: v.y + step }; redraw(); break;
      case "ArrowDown": view.current = { ...v, y: v.y - step }; redraw(); break;
      default: return;
    }
    e.preventDefault();
  };

  const hovered = hover ? byId.get(hover.id) : null;
  const sevPresent = SEVERITY_ORDER.filter((s) => nodes.some((n) => n.severity === s));

  return (
    <div className={`map-canvas${full ? " is-full" : ""}`} ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="map-canvas-surface"
        role="img"
        aria-label={`${ariaLabel}: ${nodes.length} types, ${edges.length} dependencies${sevPresent.length ? `, findings: ${sevPresent.join(", ")}` : ""}. Keyboard: arrows pan, plus and minus zoom, 0 fits, R re-lays out, F toggles fullscreen.`}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        onPointerLeave={() => { if (!gesture.current) { hoverRef.current = null; setHover(null); redraw(); } }}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        style={{ cursor: hover ? "pointer" : "grab" }}
      />

      <div className="map-toolbar" role="toolbar" aria-label="Map controls">
        <button className="map-tool" onClick={() => zoomCenter(1.25)} aria-label="Zoom in" title="Zoom in (+)"><Icon.plus size={15} /></button>
        <button className="map-tool" onClick={() => zoomCenter(0.8)} aria-label="Zoom out" title="Zoom out (−)"><Icon.minus size={15} /></button>
        <button className="map-tool" onClick={() => fit()} aria-label="Fit to view" title="Fit to view (0)"><Icon.fit size={15} /></button>
        <span className="map-tool-sep" aria-hidden="true" />
        <button className="map-tool map-tool-text" onClick={relayout} title="Release pinned nodes and lay out again (R)">
          <Icon.relayout size={15} />
          <span>Re-layout</span>
        </button>
        <button className="map-tool" onClick={toggleFull} aria-label={full ? "Exit fullscreen" : "Fullscreen"} title={full ? "Exit fullscreen (F)" : "Fullscreen (F)"}>
          {full ? <Icon.shrink size={15} /> : <Icon.expand size={15} />}
        </button>
      </div>

      <div className="map-hint" aria-hidden="true">
        {pinned > 0 ? (
          <>
            <Icon.pin size={12} /> {pinned} pinned · double-click a node to release it
          </>
        ) : (
          <>Drag to pan · scroll to zoom · drag a node to reshape</>
        )}
      </div>

      {hovered && hover && !gesture.current && (
        <div
          className="map-tip"
          style={{ left: Math.min(hover.x + 14, size.current.w - 260), top: Math.min(hover.y + 14, size.current.h - 90) }}
          aria-hidden="true"
        >
          <strong>{hovered.label}</strong>
          {hovered.file && <span className="map-tip-file">{hovered.file}</span>}
          <span className="map-tip-meta">
            {hovered.degree} connections · {hovered.methods} methods
            {hovered.severity && <span className={`tag tag-${hovered.severity}`}>{hovered.severity}</span>}
          </span>
        </div>
      )}

      <div className="sr-only" aria-live="polite">{announce}</div>
    </div>
  );
}
